// Ejemplos con la Raspberry Pi Pico (RP2040, arduino-pico) montada en una protoboard completa.
//
// La Pico ocupa las columnas 3–22 sobre el canal: sus pines quedan en las filas c y h y de cada uno salen dos
// agujeros libres (a/b arriba, i/j abajo). Fila de abajo (pines 1–20): GP0 en la columna 3 … GP15 en la 22.
// Fila de arriba (pines 40–21): VBUS en la 3, VSYS 4, GND 5, 3V3_EN 6, 3V3 7, ADC_VREF 8, GP28 9, AGND 10,
// GP27 11, GP26 12, RUN 13, GP22 14, GND 15, GP21 16, GP20 17, GP19 18, GP18 19, GND 20, GP17 21, GP16 22.
import { at, part, wires } from './helpers.js';

const bb = () => ({ id: 'bb1', type: 'breadboard', x: 0, z: 0, rot: 0, props: { size: 'full' } });
const pico = (b) => part('pico', 'pi-pico', {}, at(b, 'h3', 'pi-pico', { pin: 'GP0' }));
// Riel: agujero n de la protoboard completa → columna floor((n − 1) / 5) · 6 + (n − 1) % 5 + 3.

export const EXAMPLES = [
  {
    id: 'pico-blink',
    category: 'Raspberry Pi Pico',
    name: 'Pico: parpadeo (Blink)',
    description: 'El LED integrado de la Raspberry Pi Pico (GP25, LED_BUILTIN) se enciende y apaga cada medio segundo.',
    project: {
      name: 'Pico: parpadeo',
      files: {
        'sketch.ino': `// Parpadeo con el LED integrado de la Raspberry Pi Pico (GP25).
// En arduino-pico LED_BUILTIN es 25 y Serial es el puerto USB.

void setup() {
  pinMode(LED_BUILTIN, OUTPUT);
  Serial.begin(115200);
}

void loop() {
  digitalWrite(LED_BUILTIN, HIGH);
  delay(500);
  digitalWrite(LED_BUILTIN, LOW);
  delay(500);
  Serial.print("Encendida hace ");
  Serial.print(millis() / 1000);
  Serial.println(" s");
}
`,
      },
      parts: [{ id: 'pico', type: 'pi-pico', x: 0, z: 0, rot: 0, props: {} }],
      wires: [],
    },
  },
  {
    id: 'pico-boton-led',
    category: 'Raspberry Pi Pico',
    name: 'Pico: botón y LED',
    description: 'Un botón en GP14 (con la pull-up interna) enciende un LED en GP15. Los GPIO de la Pico trabajan a 3.3 V.',
    project: (() => {
      const b = bb();
      return {
        name: 'Pico: botón y LED',
        files: {
          'sketch.ino': `// Botón en GP14 y LED en GP15 (con su resistencia de 220 Ω).
// El botón va a GND: con INPUT_PULLUP el pin lee HIGH suelto y LOW al presionarlo.
// Los GPIO de la Pico son de 3.3 V: con 220 Ω el LED recibe unos 4 mA.

const int BOTON = 14;
const int LED = 15;

void setup() {
  pinMode(BOTON, INPUT_PULLUP);
  pinMode(LED, OUTPUT);
}

void loop() {
  bool presionado = digitalRead(BOTON) == LOW;
  digitalWrite(LED, presionado ? HIGH : LOW);
  delay(10);   // revisa el botón 100 veces por segundo; mientras tanto el RP2040 descansa
}
`,
        },
        parts: [
          b,
          pico(b),
          part('btn1', 'pushbutton', { color: 'blue' }, at(b, 'e33', 'pushbutton')),
          part('led1', 'led', { color: 'red' }, at(b, 'f40', 'led')),
          part('r1', 'resistor', { value: 220 }, at(b, 'h41', 'resistor')),
        ],
        wires: wires([
          ['bb1:j21', 'bb1:j33', 'yellow'],
          ['bb1:j35', 'bb1:bn28', 'black'],
          ['bb1:j22', 'bb1:j45', 'green'],
          ['bb1:j40', 'bb1:bn33', 'black'],
          ['bb1:j20', 'bb1:bn15', 'black'],
        ]),
      };
    })(),
  },
  {
    id: 'pico-potenciometro',
    category: 'Raspberry Pi Pico',
    name: 'Pico: potenciómetro y PWM',
    description: 'Lee un potenciómetro en A0 (GP26) con el ADC de 12 bits y ajusta con PWM el brillo de un LED en GP15.',
    project: (() => {
      const b = bb();
      return {
        name: 'Pico: potenciómetro',
        files: {
          'sketch.ino': `// Potenciómetro en A0 (GP26) y LED con PWM en GP15.
// El ADC del RP2040 es de 12 bits (0–4095) y mide de 0 a 3.3 V: el potenciómetro va a 3V3, nunca a 5 V.

const int POT = A0;     // GP26
const int LED = 15;

void setup() {
  Serial.begin(115200);
  analogReadResolution(12);   // por omisión arduino-pico da 10 bits, como un Uno
  pinMode(LED, OUTPUT);
}

void loop() {
  int lectura = analogRead(POT);               // 0–4095
  float volts = lectura * 3.3 / 4095;
  analogWrite(LED, map(lectura, 0, 4095, 0, 255));
  Serial.print("ADC: ");
  Serial.print(lectura);
  Serial.print("  ");
  Serial.print(volts, 2);
  Serial.println(" V");
  delay(200);
}
`,
        },
        parts: [
          b,
          pico(b),
          part('pot1', 'potentiometer', { value: 0.4 }, at(b, 'b39', 'potentiometer')),
          part('led1', 'led', { color: 'yellow' }, at(b, 'f40', 'led')),
          part('r1', 'resistor', { value: 220 }, at(b, 'h41', 'resistor')),
        ],
        wires: wires([
          ['bb1:a7', 'bb1:tp5', 'red'],
          ['bb1:a5', 'bb1:tn3', 'black'],
          ['bb1:a41', 'bb1:tp33', 'red'],
          ['bb1:a39', 'bb1:tn31', 'black'],
          ['bb1:a40', 'bb1:a12', 'orange'],
          ['bb1:j22', 'bb1:j45', 'green'],
          ['bb1:j40', 'bb1:bn33', 'black'],
          ['bb1:j20', 'bb1:bn15', 'black'],
        ]),
      };
    })(),
  },
  {
    id: 'pico-servo',
    category: 'Raspberry Pi Pico',
    name: 'Pico: servomotor',
    description: 'Un servo SG90 en GP15 barre de 0° a 180°. Se alimenta de VBUS (5 V del USB); la señal de 3.3 V le basta.',
    project: (() => {
      const b = bb();
      return {
        name: 'Pico: servo',
        files: {
          'sketch.ino': `// Servo en GP15. En la Pico, la librería Servo genera los pulsos con un PIO.
// arduino-pico usa por omisión pulsos de 1000 a 2000 µs (un SG90 gira solo unos 90°):
// con attach(pin, 544, 2400) tiene el mismo rango que en un Arduino Uno.
#include <Servo.h>

Servo servo;

void setup() {
  servo.attach(15, 544, 2400);
}

void loop() {
  for (int angulo = 0; angulo <= 180; angulo++) {
    servo.write(angulo);
    delay(10);
  }
  for (int angulo = 180; angulo >= 0; angulo--) {
    servo.write(angulo);
    delay(10);
  }
}
`,
        },
        parts: [b, pico(b), part('sv1', 'servo', {}, { x: 70, z: 48, rot: 0 })],
        wires: wires([
          ['bb1:a3', 'bb1:tp1', 'red'],
          ['bb1:a5', 'bb1:tn3', 'black'],
          ['bb1:j22', 'sv1:PWM', 'orange'],
          ['bb1:tp45', 'sv1:V+', 'red'],
          ['bb1:tn46', 'sv1:GND', 'black'],
        ]),
      };
    })(),
  },
  {
    id: 'pico-neopixel',
    category: 'Raspberry Pi Pico',
    name: 'Pico: tira NeoPixel',
    description: 'Un arcoíris que corre por una tira de 8 NeoPixel (WS2812B) en GP16, con Adafruit_NeoPixel (usa un PIO del RP2040).',
    project: (() => {
      const b = bb();
      return {
        name: 'Pico: NeoPixel',
        files: {
          'sketch.ino': `// Arcoíris en una tira de 8 NeoPixel (WS2812B) en GP16.
// En la Pico, Adafruit_NeoPixel genera la señal de 800 kHz con un PIO.
// La tira va a VBUS (5 V): el WS2812B se alimenta con 3.5 a 5.3 V. Su entrada pide 0.7 · VDD (3.5 V con 5 V);
// los 3.3 V de la Pico suelen bastar, pero para algo confiable usa un convertidor de nivel (74AHCT125).
#include <Adafruit_NeoPixel.h>

const int PIN_TIRA = 16;
const int LEDS = 8;
Adafruit_NeoPixel tira(LEDS, PIN_TIRA, NEO_GRB + NEO_KHZ800);

void setup() {
  tira.begin();
  tira.setBrightness(80);
}

void loop() {
  static uint16_t tono = 0;
  for (int i = 0; i < LEDS; i++) {
    uint16_t t = tono + i * 65536L / LEDS;
    tira.setPixelColor(i, tira.gamma32(tira.ColorHSV(t)));
  }
  tira.show();
  tono += 1024;
  delay(30);
}
`,
        },
        parts: [b, pico(b), part('np1', 'neopixel-stick', {}, { x: 72, z: -48, rot: 0 })],
        wires: wires([
          ['bb1:b22', 'np1:DIN', 'green'],
          ['bb1:a3', 'bb1:tp1', 'red'],
          ['bb1:a5', 'bb1:tn3', 'black'],
          ['bb1:tp30', 'np1:VCC', 'red'],
          ['bb1:tn31', 'np1:GND', 'black'],
        ]),
      };
    })(),
  },
  {
    id: 'pico-oled',
    category: 'Raspberry Pi Pico',
    name: 'Pico: pantalla OLED I²C',
    description: 'Una OLED SSD1306 de 128×64 en el I²C por omisión de la Pico (Wire: GP4 SDA, GP5 SCL), alimentada con 3V3.',
    project: (() => {
      const b = bb();
      return {
        name: 'Pico: OLED',
        files: {
          'sketch.ino': `// OLED SSD1306 128×64 por I²C. En arduino-pico, Wire usa GP4 (SDA) y GP5 (SCL).
// El módulo va a 3V3: así sus pull-ups de SDA y SCL también quedan en 3.3 V.
#include <Wire.h>
#include <Adafruit_GFX.h>
#include <Adafruit_SSD1306.h>

Adafruit_SSD1306 pantalla(128, 64, &Wire, -1);

void setup() {
  Serial.begin(115200);
  if (!pantalla.begin(SSD1306_SWITCHCAPVCC, 0x3C)) {
    Serial.println("No se encontró la pantalla");
    while (true) delay(100);
  }
}

void loop() {
  pantalla.clearDisplay();
  pantalla.setTextColor(SSD1306_WHITE);
  pantalla.setTextSize(2);
  pantalla.setCursor(0, 0);
  pantalla.println("Pico");
  pantalla.setTextSize(1);
  pantalla.println("RP2040 a 125 MHz");
  pantalla.setCursor(0, 40);
  pantalla.print("Segundos: ");
  pantalla.println(millis() / 1000);
  pantalla.display();
  delay(500);
}
`,
        },
        parts: [b, pico(b), part('oled1', 'oled-ssd1306', {}, { x: 64, z: 50, rot: 0 })],
        wires: wires([
          ['bb1:a7', 'bb1:tp5', 'red'],
          ['bb1:a5', 'bb1:tn3', 'black'],
          ['bb1:tp40', 'oled1:VCC', 'red'],
          ['bb1:tn41', 'oled1:GND', 'black'],
          ['bb1:j8', 'oled1:SDA', 'blue'],
          ['bb1:j9', 'oled1:SCL', 'yellow'],
        ]),
      };
    })(),
  },
  {
    id: 'pico-lcd',
    category: 'Raspberry Pi Pico',
    name: 'Pico: LCD I²C 16×2',
    description: 'Una LCD de 16×2 con adaptador I²C (PCF8574) en GP4/GP5. La LCD va a VBUS: necesita 5 V para verse.',
    project: (() => {
      const b = bb();
      return {
        name: 'Pico: LCD I²C',
        files: {
          'sketch.ino': `// LCD 16×2 con adaptador I²C (PCF8574, dirección 0x27) en Wire: GP4 (SDA) y GP5 (SCL).
// La LCD necesita 5 V (VBUS) para verse bien. SDA y SCL quedan en 3.3 V por las pull-ups de la Pico;
// si tu adaptador trae pull-ups a 5 V, quítalas o usa un convertidor de nivel: la Pico no tolera 5 V.
#include <Wire.h>
#include <LiquidCrystal_I2C.h>

LiquidCrystal_I2C lcd(0x27, 16, 2);

void setup() {
  lcd.init();
  lcd.backlight();
  lcd.setCursor(0, 0);
  lcd.print("Raspberry Pi");
  lcd.setCursor(0, 1);
  lcd.print("Pico + LCD I2C");
  delay(1500);
}

void loop() {
  lcd.setCursor(0, 1);
  lcd.print("Tiempo: ");
  lcd.print(millis() / 1000);
  lcd.print(" s     ");
  delay(250);
}
`,
        },
        parts: [b, pico(b), part('lcd1', 'lcd-i2c', { address: '0x27' }, { x: 66, z: 58, rot: 0 })],
        wires: wires([
          ['bb1:a3', 'bb1:tp1', 'red'],
          ['bb1:a5', 'bb1:tn3', 'black'],
          ['bb1:tp45', 'lcd1:VCC', 'red'],
          ['bb1:tn46', 'lcd1:GND', 'black'],
          ['bb1:j8', 'lcd1:SDA', 'blue'],
          ['bb1:j9', 'lcd1:SCL', 'yellow'],
        ]),
      };
    })(),
  },
];
